const SUBSCRIBED_KEY = 'katshu:newsletter-popup:subscribed';
const HIDDEN_UNTIL_KEY = 'katshu:newsletter-popup:hidden-until';

// KATSHU · 5 sept 2026 — SALIDA EN MÓVIL. Ajustes del gesto, todos en un sitio.
// (Ver el bloque largo sobre armMobileExitIntent() más abajo para el porqué.)
const SCROLL_MIN_PAGINA = 3000;      // px de recorrido reales antes de fiarnos de la altura
const SCROLL_MIN_PROFUNDIDAD = 1200; // px que hay que haber bajado de verdad
const MOBILE_EXIT_ARM_SECONDS = 10; // no se arma hasta pasados 10 s: nadie "se va" en el segundo 2
const MOBILE_EXIT_MIN_DEPTH = 400; // px que hay que haber bajado antes: prueba de que miró algo
const MOBILE_EXIT_SPEED = 900; // px/s hacia ARRIBA que delatan el golpe de pulgar de salida

/**
 * Drives the automatic show/hide behavior of the newsletter popup.
 * The popup markup and open/close mechanics themselves are handled by
 * the existing `dialog-component` (see dialog.js) — this only decides
 * *when* to call its public showDialog()/closeDialog() methods.
 */
class NewsletterPopupController {
  /** @param {HTMLElement} root - the <dialog-component> element */
  constructor(root) {
    this.root = root;
    this.delaySeconds = Number(root.dataset.delaySeconds) || 8;
    this.scrollPercentage = Number(root.dataset.scrollPercentage) || 35;
    this.daysToHide = Number(root.dataset.daysToHide) || 14;
    this.postedSuccessfully = root.dataset.postedSuccessfully === 'true';
    this.sectionId = root.dataset.sectionId;

    this.timeoutId = null;
    this.handleScroll = this.handleScroll.bind(this);
    this.handleExitIntent = this.handleExitIntent.bind(this);
    this.handleMobileExitIntent = this.handleMobileExitIntent.bind(this);
    this.maxScrollDepth = 0;
    this.lastScrollY = 0;
    this.lastScrollAt = 0;
    this.mobileExitArmedAt = 0;

    root.addEventListener('dialog:close', () => this.handleClose());

    this.init();
  }

  init() {
    if (this.postedSuccessfully) {
      this.markSubscribed();
      this.open();
      return;
    }

    if (this.isSubscribed() || this.isSnoozed()) return;

    if (window.Shopify && window.Shopify.designMode) {
      this.bindDesignModeEvents();
      return;
    }

    // 31 ago 2026 — EN LA FICHA NO SE INTERRUMPE A LOS 8 SEGUNDOS.
    // El 91 % del tráfico entra por móvil y aterriza aquí desde el anuncio: a los
    // 8 s el pop-up tapaba el producto, el precio y el botón para pedir el correo
    // ANTES de que supieran qué miraban. En la ficha se espera 35 s; el disparador
    // por scroll (35 %) sigue activo. En el resto de páginas, todo igual.
    const enFicha = window.location.pathname.indexOf('/products/') !== -1;
    const espera = enFicha ? Math.max(this.delaySeconds, 35) : this.delaySeconds;
    this.timeoutId = window.setTimeout(() => this.abrirSiElCarritoEstaVacio(), espera * 1000);
    window.addEventListener('scroll', this.handleScroll, { passive: true });

    // Desktop/trackpad visitors get an earlier, higher-intent trigger: leaving
    // toward the browser chrome (about to close the tab / switch away) opens
    // the popup immediately instead of making them wait out the delay/scroll.
    // Touch devices have no such signal, so this never fires on mobile.
    if (window.matchMedia('(pointer: fine)').matches) {
      document.addEventListener('mouseout', this.handleExitIntent);
    } else {
      // Mismo criterio que el pie de este archivo: el pop-up es el negocio y la
      // salida en móvil es una mejora. Si por lo que sea reventara, no puede
      // llevarse por delante el reloj ni el scroll, que ya funcionan hoy.
      try {
        this.armMobileExitIntent();
      } catch {
        /* sin salida en móvil; el resto del pop-up sigue intacto */
      }
    }
  }

  // ══════════════════════════════════════════════════════════════════════════
  // KATSHU · 5 sept 2026 — EL QUE SE VA EN MÓVIL NO TENÍA QUIEN LO PARARA.
  //
  // QUÉ FALTABA: el exit-intent de arriba vive de `mouseout`, que en una
  // pantalla táctil NO EXISTE. En móvil —el 91 % del tráfico— los únicos
  // disparadores eran el reloj (35 s en la ficha) y el scroll al 25 %.
  //
  // POR QUÉ IMPORTA, con un caso con nombre: Cristina (`#1010`) entró desde el
  // anuncio el 25 de agosto, se fue SIN dejar el correo y estuvimos ONCE DÍAS
  // sin poder escribirle. Volvió el 5 de septiembre y compró 89,90 €. La trajo
  // de vuelta el anuncio, no nosotros. Su ficha de cliente se creó el día de la
  // compra: nunca la tuvimos.
  //
  // QUÉ NO SE TOCA, A PROPÓSITO: los 35 s de la ficha y el 25 % de scroll se
  // quedan igual. Se decidieron el 31 ago para no tapar el producto a los 8 s,
  // y siguen siendo correctos: quien COMPRA lo hace en minutos y no se le debe
  // interrumpir. Esto no adelanta nada — solo añade una salida más.
  //
  // EL GESTO: bajar y luego subir de golpe. Es el equivalente táctil de llevar
  // el ratón a la barra del navegador. Se piden las tres cosas a la vez para no
  // saltar sobre alguien que solo está leyendo:
  //   1) llevar ya 10 s en la página,  2) haber bajado 400 px en algún momento,
  //   3) subir a más de 900 px/s.
  //
  // REVERTIR: borrar la rama `else` de init(), estos dos métodos, la línea de
  // clearTriggers() y las tres constantes MOBILE_EXIT_* de la cabecera. No hay
  // nada más enganchado.
  // ══════════════════════════════════════════════════════════════════════════
  armMobileExitIntent() {
    this.mobileExitArmedAt = Date.now() + MOBILE_EXIT_ARM_SECONDS * 1000;
    this.lastScrollY = window.scrollY;
    this.lastScrollAt = Date.now();
    window.addEventListener('scroll', this.handleMobileExitIntent, { passive: true });
  }

  handleMobileExitIntent() {
    const now = Date.now();
    const y = window.scrollY;
    const elapsed = now - this.lastScrollAt;

    if (y > this.maxScrollDepth) this.maxScrollDepth = y;

    const subida = this.lastScrollY - y;
    this.lastScrollY = y;
    this.lastScrollAt = now;

    if (elapsed <= 0 || now < this.mobileExitArmedAt) return;
    if (this.maxScrollDepth < MOBILE_EXIT_MIN_DEPTH) return;
    if (subida <= 0) return;

    const velocidad = (subida / elapsed) * 1000;
    if (velocidad >= MOBILE_EXIT_SPEED) this.abrirSiElCarritoEstaVacio();
  }

  // ══════════════════════════════════════════════════════════════════════════
  // KATSHU · 6 sept 2026 — EL POP-UP DISPARABA ENCIMA DEL CARRITO.
  //
  // QUÉ PASABA: este archivo no mencionaba el carrito en ninguna línea. El
  // reloj de 35 s en la ficha saltaba igual aunque la persona YA hubiera
  // añadido la fuente. Le pasó a Cristina (`#1010`): compró en ~60 s y el
  // pop-up le salió en plena compra.
  //
  // POR QUÉ IMPORTA, con el número delante: en la semana del 30 ago al 6 sept
  // el embudo fue 37 carritos → 12 al pago. VEINTICINCO personas que ya habían
  // aceptado los 89,90 € no llegaron ni a empezar el pago. Y a esas justamente
  // les tapábamos el carrito con un modal que además les decía que hay 10 €
  // de descuento: se van a buscar un código que llega por correo, y ese
  // desvío sale del embudo.
  //
  // QUÉ NO SE TOCA: los 35 s, el 25 % de scroll y las dos salidas (ratón y
  // móvil) se quedan EXACTAMENTE igual. Esto no cambia cuándo se abre —
  // cambia a QUIÉN se le abre. Quien no tiene nada en el carrito lo ve igual.
  //
  // A PRUEBA DE FALLOS: si la consulta a /cart.js falla o tarda, se abre como
  // hoy. El pop-up es el negocio; esta guarda es una mejora y no puede
  // llevárselo por delante.
  //
  // ↩️ REVERTIR: sustituir las 4 llamadas a `abrirSiElCarritoEstaVacio()` por
  //    `open()` y borrar este método. Son 4 líneas, en el reloj (init), el
  //    scroll (handleScroll) y las dos salidas (handleExitIntent y
  //    handleMobileExitIntent).
  // ══════════════════════════════════════════════════════════════════════════
  async abrirSiElCarritoEstaVacio() {
    try {
      const respuesta = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (respuesta.ok) {
        const carrito = await respuesta.json();
        if (carrito && carrito.item_count > 0) return;
      }
    } catch {
      /* no se pudo consultar el carrito: se abre como hoy, sin regresión */
    }
    this.open();
  }

  // ══════════════════════════════════════════════════════════════════════════
  // KATSHU · 6 sept 2026 — EL POP-UP SALTABA EN EL PRIMER SEGUNDO AL LLEGAR
  // DESDE UN ANUNCIO. Lo vio David en vivo: pulsó «Comprar» en el anuncio de
  // TikTok y el pop-up ya estaba encima de la ficha.
  //
  // POR QUÉ: el navegador INTERNO de TikTok (y el de Instagram) informa una
  // `scrollHeight` que todavía no es la definitiva — la ficha mide 12.481 px
  // pero durante los primeros instantes puede reportar unos cientos. Como el
  // disparador dividía entre esa altura, un arrastre de 40 px ya salía como
  // «ha bajado el 35 %» y abría el pop-up. En un navegador normal no pasa:
  // por eso llevaba semanas sin verse.
  //
  // POR QUÉ IMPORTA: es el 100 % del tráfico que PAGAMOS. Llega de un anuncio,
  // aterriza, y lo primero que ve es un modal encima del producto — con el
  // aviso de cookies ya tapando el precio. Dos carteles antes de ver nada.
  //
  // LAS DOS GUARDAS, y ninguna toca el comportamiento normal:
  //   · la página tiene que medir de verdad (si no, aún no está medida)
  //   · y hay que haber bajado de verdad, en píxeles, no en porcentaje
  // En la ficha real el 35 % son ~4.000 px, muy por encima del mínimo: quien
  // lee de verdad sigue viendo el pop-up igual que ayer.
  //
  // ↩️ REVERTIR: borrar las dos comprobaciones de abajo y las dos constantes
  //    SCROLL_MIN_* de la cabecera. El resto queda igual.
  // ══════════════════════════════════════════════════════════════════════════
  handleScroll() {
    const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight;
    if (scrollableHeight < SCROLL_MIN_PAGINA) return;
    if (window.scrollY < SCROLL_MIN_PROFUNDIDAD) return;

    const scrolledPercentage = (window.scrollY / scrollableHeight) * 100;
    if (scrolledPercentage >= this.scrollPercentage) {
      this.abrirSiElCarritoEstaVacio();
    }
  }

  handleExitIntent(event) {
    if (event.clientY > 0 || event.relatedTarget) return;
    this.abrirSiElCarritoEstaVacio();
  }

  // ══════════════════════════════════════════════════════════════════════════
  // KATSHU · 25 ago 2026 — RETIRADA LA ESPERA POR EL BANNER DE COOKIES.
  //
  // QUÉ HABÍA: desde el 24 jul (commit 2b18d55) open() comprobaba si el aviso
  // de cookies tapaba el centro de la pantalla y, si lo tapaba, NO abría: se
  // quedaba sondeando cada 300 ms "hasta que se quitara".
  //
  // POR QUÉ ERA FATAL: el aviso de Shopify NO se puede cerrar en esta tienda
  // —«Rechazar» no lo cierra y no escribe cookie de consentimiento (verificado
  // dos veces el 25 ago)—. En móvil ese aviso ocupa de y=385 a y=812 en una
  // pantalla de 812, o sea el centro. Resultado: en MÓVIL el pop-up esperaba
  // eternamente y NO SE MOSTRABA NUNCA. En escritorio el aviso queda fuera de
  // pantalla (top:-64) y por eso allí sí salía — de ahí que entraran leads a
  // goteo y pareciera que funcionaba. Nuestro tráfico es casi todo móvil.
  //
  // POR QUÉ LA ESPERA SOBRABA DE TODAS FORMAS: el diálogo se abre con
  // `dialog.showModal()` (assets/dialog.js), que pinta en la TOP LAYER del
  // navegador — por encima de cualquier z-index, incluido el 2.000.000 del
  // aviso. El aviso es un <section> con position:fixed: no puede taparlo.
  // La premisa del arreglo del 24 jul era falsa.
  //
  // REVERTIR: recuperar `isCenterCoveredByPrivacyBanner` y
  // `waitForPrivacyBannerToClear` del commit 2b18d55 y volver a llamarlas aquí.
  // ══════════════════════════════════════════════════════════════════════════
  open() {
    this.clearTriggers();
    this.root.showDialog?.();
  }

  clearTriggers() {
    if (this.timeoutId) {
      window.clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('scroll', this.handleMobileExitIntent);
    document.removeEventListener('mouseout', this.handleExitIntent);
  }

  handleClose() {
    this.clearTriggers();

    // Avoid snoozing the popup for real visitors just because a merchant
    // opened/closed it while browsing sections in the theme editor.
    if (window.Shopify && window.Shopify.designMode) return;

    if (!this.isSubscribed()) {
      this.snooze();
    }
  }

  bindDesignModeEvents() {
    document.addEventListener('shopify:section:select', (event) => {
      if (event.target?.id === this.sectionId) this.root.showDialog?.();
    });
    document.addEventListener('shopify:section:deselect', (event) => {
      if (event.target?.id === this.sectionId) this.root.closeDialog?.();
    });
  }

  isSubscribed() {
    try {
      return window.localStorage.getItem(SUBSCRIBED_KEY) === '1';
    } catch {
      return false;
    }
  }

  markSubscribed() {
    try {
      window.localStorage.setItem(SUBSCRIBED_KEY, '1');
    } catch {
      // Storage unavailable (e.g. private browsing) — popup may reappear, non-critical.
    }
  }

  isSnoozed() {
    try {
      const hiddenUntil = Number(window.localStorage.getItem(HIDDEN_UNTIL_KEY));
      return Boolean(hiddenUntil) && Date.now() < hiddenUntil;
    } catch {
      return false;
    }
  }

  snooze() {
    try {
      const hiddenUntil = Date.now() + this.daysToHide * 24 * 60 * 60 * 1000;
      window.localStorage.setItem(HIDDEN_UNTIL_KEY, String(hiddenUntil));
    } catch {
      // Storage unavailable — ignore, popup may reappear sooner than intended.
    }
  }
}


/* ────────────────────────────────────────────────────────────────────────────
 * Aviso de correo mal escrito.
 *
 * Por qué existe (30 ago 2026): tres leads pagados entraron con el dominio mal
 * tecleado y ninguno recibió su código. Y los dos fallos NO son el mismo:
 *   · `aziertaabogados.comi` — el dominio no existe. El correo rebota.
 *   · `hotnail.com`          — el dominio SÍ existe (typosquat aparcado, con MX
 *                              propio). El correo no rebota: lo recibe otro,
 *                              con el nombre de la clienta y su descuento dentro.
 * `type="email"` no ve ninguno de los dos: los dos tienen forma válida.
 *
 * REGLA QUE MANDA SOBRE TODO LO DEMÁS: esto SUGIERE, no bloquea nunca.
 * Entre nuestras clientas hay dominios corporativos reales y raros
 * (`icam.es`, `icali.es`). Un validador duro nos costaría más leads que los typos.
 * ──────────────────────────────────────────────────────────────────────────── */

const LAST_EMAIL_KEY = 'katshu:newsletter-popup:last-email';

const KNOWN_DOMAINS = [
  'gmail.com', 'hotmail.com', 'hotmail.es', 'outlook.com', 'outlook.es',
  'yahoo.com', 'yahoo.es', 'icloud.com', 'me.com', 'live.com', 'msn.com',
  'aol.com', 'gmx.es', 'protonmail.com', 'proton.me', 'telefonica.net',
  'terra.es', 'movistar.es', 'orange.es', 'ya.com',
];

// Se aceptan tal cual. Un dominio con un TLD que NO esté aquí solo se comenta
// si está a un carácter de uno de estos; si no se parece a ninguno, se calla.
const KNOWN_TLDS = [
  'com', 'es', 'net', 'org', 'eu', 'cat', 'gal', 'eus', 'co', 'io', 'me',
  'info', 'biz', 'pt', 'fr', 'it', 'de', 'uk', 'mx', 'ar', 'cl', 'edu', 'gob',
];

/**
 * Distancia de edición contando la TRANSPOSICIÓN como un solo fallo
 * (Damerau-Levenshtein restringida), con corte en cuanto pasa de `max`.
 *
 * Lo de la transposición no es un adorno: cambiar dos letras de sitio es el
 * error de tecleo más común que existe. Con Levenshtein a secas, `gmial.com`
 * queda a distancia 2 de `gmail.com` y se nos escapa; contándola, queda a 1.
 */
function editDistance(a, b, max) {
  if (Math.abs(a.length - b.length) > max) return max + 1;

  let prevPrev = [];
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);

  for (let i = 1; i <= a.length; i++) {
    const row = [i];
    let best = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let value = Math.min(prev[j] + 1, row[j - 1] + 1, prev[j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        value = Math.min(value, prevPrev[j - 2] + 1);
      }
      row[j] = value;
      if (value < best) best = value;
    }
    if (best > max) return max + 1;
    prevPrev = prev;
    prev = row;
  }
  return prev[b.length];
}

/** Devuelve el dominio que probablemente quería escribir, o null si está bien. */
function suggestDomain(domain) {
  if (!domain || KNOWN_DOMAINS.includes(domain)) return null;

  // 1. ¿Es un dominio conocido con una letra cambiada? (hotnail → hotmail)
  for (const known of KNOWN_DOMAINS) {
    if (editDistance(domain, known, 1) <= 1) return known;
  }

  // 2. ¿Es un dominio cualquiera con el TLD mal escrito? (.comi → .com)
  const cut = domain.lastIndexOf('.');
  if (cut <= 0) return null;
  const name = domain.slice(0, cut);
  const tld = domain.slice(cut + 1);
  if (KNOWN_TLDS.includes(tld)) return null;
  for (const known of KNOWN_TLDS) {
    if (editDistance(tld, known, 1) <= 1) return `${name}.${known}`;
  }

  return null;
}

class NewsletterEmailGuard {
  constructor(form) {
    this.form = form;
    this.input = form.querySelector('input[type="email"]');
    this.hint = form.querySelector('[data-newsletter-suggest]');
    if (!this.input || !this.hint) return;

    // Correo por el que ya se avisó una vez. Si insiste con el mismo, pasa.
    this.warnedFor = null;

    this.input.addEventListener('blur', () => this.check());
    this.input.addEventListener('input', () => this.hide());
    this.form.addEventListener('submit', (event) => this.onSubmit(event));
  }

  /** El dominio que probablemente quería, o null. */
  betterFor(value) {
    const at = value.lastIndexOf('@');
    if (at < 1 || at === value.length - 1) return null;
    return suggestDomain(value.slice(at + 1).toLowerCase());
  }

  check() {
    const value = this.input.value.trim();
    const better = this.betterFor(value);
    if (!better) return this.hide();
    this.show(value.slice(0, value.lastIndexOf('@') + 1) + better, better);
  }

  /**
   * ⚠️ ESTE es el momento que importa, y el que faltaba.
   * En el móvil se toca «enviar» sin salir del campo: el aviso por `blur` no
   * llega a dispararse nunca y el correo mal escrito se va tal cual. Aquí se
   * para UNA sola vez para enseñarle la corrección.
   * Si vuelve a darle con el mismo correo, se envía: avisamos, no mandamos.
   */
  onSubmit(event) {
    const value = this.input.value.trim();
    const better = this.betterFor(value);

    if (better && this.warnedFor !== value) {
      event.preventDefault();
      this.warnedFor = value;
      this.show(value.slice(0, value.lastIndexOf('@') + 1) + better, better);
      this.input.focus();
      return;
    }

    this.remember();
  }

  show(fixed, domain) {
    this.hint.textContent = '¿Querías decir ';
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = domain;
    button.addEventListener('click', () => {
      this.input.value = fixed;
      this.warnedFor = null;
      this.hide();
      this.input.focus();
    });
    this.hint.appendChild(button);
    this.hint.appendChild(document.createTextNode('?'));
    this.hint.hidden = false;
  }

  hide() {
    this.hint.hidden = true;
    this.hint.textContent = '';
  }

  /** El formulario de cliente de Shopify no devuelve el email enviado: lo guardamos. */
  remember() {
    try {
      sessionStorage.setItem(LAST_EMAIL_KEY, this.input.value.trim());
    } catch (error) {
      /* modo privado sin almacenamiento: simplemente no habrá eco */
    }
  }
}

/** En la pantalla de "ya está", le devolvemos el correo para que lo lea. */
function renderEmailEcho() {
  const echo = document.querySelector('[data-newsletter-echo]');
  if (!echo) return;

  let email = null;
  try {
    email = sessionStorage.getItem(LAST_EMAIL_KEY);
    sessionStorage.removeItem(LAST_EMAIL_KEY);
  } catch (error) {
    return;
  }
  if (!email) return;

  echo.textContent = 'Te lo mandamos a ';
  const strong = document.createElement('strong');
  strong.textContent = email;
  echo.appendChild(strong);
  echo.hidden = false;
}

function initNewsletterPopup() {
  // ⚠️ ORDEN DELIBERADO — el pop-up se arranca PRIMERO.
  // Todo lo de abajo (el aviso de correo, el eco) es una mejora; el pop-up es el
  // negocio. Si algún día algo de lo añadido revienta, no puede llevarse por
  // delante la captación de leads: por eso va después, y dentro de un try/catch.
  const root = document.getElementById('newsletter-popup');
  if (root) new NewsletterPopupController(root);

  try {
    // Se engancha a TODOS los formularios de pop-up de la página, no solo al que
    // lleva el id: el controlador mira un id concreto y hay dos instancias
    // renderizadas en la home — una se quedaría sin protección.
    document
      .querySelectorAll('.newsletter-popup__form')
      .forEach((form) => new NewsletterEmailGuard(form));
    renderEmailEcho();
  } catch (error) {
    // El aviso es prescindible; el formulario no. Si falla, se calla y ya está.
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNewsletterPopup);
} else {
  initNewsletterPopup();
}
